import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Grid, Paper, Stack } from "@mui/material";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import RefreshIcon from "@mui/icons-material/Refresh";
import isEqual from "lodash.isequal";
import { useNavigate } from "react-router-dom";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { useUnsavedChangesGuard } from "../../../contexts/hooks/useUnsavedChangesGuard";
import { emptyLocalizedFields } from "../../../config/localization";
import { uploadMedia } from "../../../services/mediaService";
import { UploadType } from "../../../types/enum";
import LocalizedInput from "../LocalizedInput";
import TextInput from "../TextInput";
import { CardHeading, cardSx } from "../CardHeading";
import { StickyFormActions } from "../StickyFormActions";

export const ValueForm = ({ props, onSave, onRefresh }) => {
  const navigate = useNavigate();
  const { openImageDialog, fileUploadDialog, showNotification } = usePreload();
  const saveInProgress = useRef(false);
  const defaultValue = useMemo(
    () => ({
      valueCode: props.row.valueCode ?? "",
      valueCodeAlternative: props.row.valueCodeAlternative ?? "",
      valueCodeOld: props.row.valueCodeOld ?? "",
      valueImage: props.row.valueImage ?? "",
      valueResourceId: props.row.valueResourceId ?? "",
      valueName: props.row.valueName || emptyLocalizedFields,
      valueDisplayName: props.row.valueDisplayName || emptyLocalizedFields,
      valueDescription: props.row.valueDescription || emptyLocalizedFields,
      valueToolTip: props.row.valueToolTip || emptyLocalizedFields,
    }),
    [props.row],
  );
  const [value, setValue] = useState(defaultValue);
  const [original, setOriginal] = useState(defaultValue);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
    setOriginal(defaultValue);
  }, [defaultValue]);

  const hasChanges = !isEqual(value, original);
  const busy = Boolean(props.loading || props.uploading || uploadingImage);
  const { confirmLeave } = useUnsavedChangesGuard(hasChanges);

  const save = async (nextValue, resetAfterCreate = false) => {
    if (saveInProgress.current) return false;
    saveInProgress.current = true;
    try {
      const saved = await onSave(nextValue);
      if (saved !== true) return false;
      if (!props.isUpdate && resetAfterCreate) {
        setValue(defaultValue);
        setOriginal(defaultValue);
      } else {
        setValue(nextValue);
        setOriginal(nextValue);
      }
      return true;
    } finally {
      saveInProgress.current = false;
    }
  };

  const openUploader = () => {
    fileUploadDialog.show({
      title: "Upload Image",
      imageLink: value.valueImage,
      handleImageUpload: async ({ uploadType, imageFile, imageLink }) => {
        setUploadingImage(true);
        fileUploadDialog.setLoading(true);
        try {
          const nextValue = {
            ...value,
            valueResourceId: "",
            valueImage: imageLink ?? value.valueImage ?? "",
          };
          if (uploadType === UploadType.FILE) {
            const result = await uploadMedia(imageFile);
            if (!result?.resourceId || !result?.resourceUrl) {
              throw new Error("Image upload failed");
            }
            nextValue.valueResourceId = result.resourceId;
            nextValue.valueImage = result.resourceUrl;
          }
          setValue(nextValue);
          await save(nextValue);
        } catch (error) {
          console.error(error);
          showNotification("Image upload failed.", "error");
        } finally {
          fileUploadDialog.setLoading(false);
          fileUploadDialog.hide();
          setUploadingImage(false);
        }
      },
    });
  };

  const update = (field) => (event) => {
    setValue((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageUrlChange = (event) => {
    const imageUrl = event.target.value;
    setValue((prev) => ({
      ...prev,
      valueImage: imageUrl,
      valueResourceId: "",
    }));
  };

  const handleCancel = async () => {
    if (!busy && (await confirmLeave())) navigate("/valuesnew");
  };

  const handleRefresh = async () => {
    if (await confirmLeave()) onRefresh();
  };

  const handleSave = () => {
    if (!busy && hasChanges) save(value, true);
  };

  const localized = (field, label, multiline = false) => (
    <Grid size={{ xs: 12, md: 6 }}>
      <LocalizedInput
        variant="modern"
        label={label}
        value={value[field]}
        onChange={(next) => setValue((prev) => ({ ...prev, [field]: next }))}
        multiline={multiline}
        rows={2}
        disabled={busy}
      />
    </Grid>
  );

  return (
    <Box
      sx={{
        "& input, & textarea, & button": {
          scrollMarginBottom: { xs: 168, sm: 96 },
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="value-details-heading"
            sx={cardSx}
          >
            <CardHeading
              id="value-details-heading"
              title="Value details"
              helper="Identify the value and manage its symbol."
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Code"
                  required
                  value={value.valueCode}
                  disabled={props.isUpdate || busy}
                  helperText={
                    props.isUpdate
                      ? "The value code cannot be changed after creation"
                      : undefined
                  }
                  onChange={update("valueCode")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Alternative code"
                  value={value.valueCodeAlternative}
                  disabled={busy}
                  onChange={update("valueCodeAlternative")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Old code"
                  value={value.valueCodeOld}
                  disabled={busy}
                  onChange={update("valueCodeOld")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                >
                  {value.valueImage && (
                    <Button
                      aria-label="Preview symbol"
                      onClick={() => openImageDialog(value.valueImage)}
                      sx={{ minWidth: 48, p: 1 }}
                    >
                      <img
                        src={value.valueImage}
                        alt=""
                        width={32}
                        height={32}
                      />
                    </Button>
                  )}
                  <TextInput
                    label="Image URL"
                    value={value.valueImage}
                    disabled={busy}
                    onChange={handleImageUrlChange}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddToPhotosIcon />}
                    onClick={openUploader}
                    disabled={busy}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Set image
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="value-localization-heading"
            sx={cardSx}
          >
            <CardHeading
              id="value-localization-heading"
              title="Localized details"
              helper="Provide names and descriptions for each language."
            />
            <Grid container spacing={3}>
              {localized("valueName", "Name")}
              {localized("valueDisplayName", "Display name", true)}
              {localized("valueDescription", "Description", true)}
              {localized("valueToolTip", "Tooltip", true)}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {onRefresh && (
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={handleRefresh}
          >
            Refresh value
          </Button>
        </Box>
      )}
      <StickyFormActions
        hasChanges={hasChanges}
        saving={busy}
        saveText={props.saveText}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </Box>
  );
};
